import React from 'react';
import { Box, Input, Typography } from '@material-ui/core/';
import styled from 'styled-components';
import { Field } from 'formik';
import { ErrorContainer } from '../../setupAccountWizard/SetupAccountStep3Verify';
import { MonthSelectorField, HealthCardSystemSelectorField, HealthCardSystem } from '../../DropDownSelector';
import { ErrorTypography } from '../commonUtilities/Typography';
import { COLOR_GREY, FONT_WEIGHT_SEMI } from '../../utilities/Material';
import { OhipInput, NovaScotiaInput, AlbertaInput, NoHealthCardSystemSelectedInput } from '../../../components/HealthCardNumberInput';
import MaskedInput from 'react-text-mask';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { styleBreakMobile } from './Globals';

export const VerifyAccountSummary = ({ isWatchman }) => {
    if (isWatchman) {
        return (
            <>
                <Box mb={1} mt={3}>
                    <Typography variant="h1">Verify Identity</Typography>
                </Box>
                <Typography>To verify your identity, enter your date of birth and your ten-digit OHIP number.</Typography>
            </>
        );
    } else {
        return (
            <>
                <Box mb={1} mt={3}>
                    <Typography variant="h2">Verify Identity</Typography>
                </Box>
                <Typography>To verify your identity, enter your province, health card number, and date of birth.</Typography>
            </>
        );
    }
};

const MuiInputWrapper = ({ isWatchman, isinvalid, filled, ...props }) => {
    return <Input {...props} />;
};
const MaskedInputWrapper = ({ isWatchman, isinvalid, filled, ...props }) => {
    return <MaskedInput {...props} />;
};
const MuiBoxWrapper = ({ isMobile, ...props }) => {
    return <Box {...props} />;
};

export const CreatePasswordTitle = ({ isWatchman, children }) => {
    if (isWatchman) {
        return <Typography variant="h1">{children}</Typography>;
    } else {
        return <Typography variant="h2">{children}</Typography>;
    }
};

const AutoWidthInputBase = styled(MuiInputWrapper)`
    font-style: normal;
    font-family: ${props => props.theme.typography.fontFamily};
    font-weight: ${FONT_WEIGHT_SEMI};
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.16px;
    border-radius: 5px;
    padding-top: 11px;
    padding-bottom: 10px;
    padding-left: 15px;
    padding-right: 15px;
    :focus-visible {
        outline: none;
    }
`;

const NedAutoWidthInput = styled(AutoWidthInputBase)`
    color: #212121;
    border: 1px solid #979797;
    &::placeholder {
        color: #979797;
        opacity: 1;
    }
    :focus {
        border: 1px solid #979797;
    }
`;

const WatchmanAutoWidthInput = styled(AutoWidthInputBase)`
    color: ${props => props.theme.palette.primary.dark};
    border: ${props =>
        props.isinvalid
            ? `1px solid ${props.theme.palette.error.main}`
            : props.filled
            ? `1px solid ${props.theme.palette.primary.dark}`
            : `1px solid ${COLOR_GREY.light}`};
    &::placeholder {
        color: ${COLOR_GREY.light};
        opacity: 1;
    }
    :focus {
        border: ${props => `1px solid ${props.theme.palette.primary.main}`};
    }
    :hover {
        border: ${props => `1px solid ${props.theme.palette.primary.dark}`};
    }
`;

const AutoWidthInput = ({ isWatchman, ...props }) => {
    if (isWatchman) {
        return <WatchmanAutoWidthInput {...props} />;
    } else {
        return <NedAutoWidthInput {...props} />;
    }
};

const FormDivSectionWrapper = styled(MuiBoxWrapper)`
    margin-top: 8px;
    display: flex;
    flex-direction: row;
    width: ${props => (props.isMobile ? '100%' : '327px')};
    justify-content: space-around;
`;

const DateInput = ({ theme, ...props }) => (
    <AutoWidthInput theme={theme} style={{ width: theme.spacing(7.125) }} mask={[/\d/, /\d/]} type="text" {...props} as={MaskedInputWrapper} />
);
const YearInput = ({ theme, ...props }) => (
    <AutoWidthInput theme={theme} style={{ width: theme.spacing(9.125) }} mask={[/\d/, /\d/, /\d/, /\d/]} type="text" {...props} as={MaskedInputWrapper} />
);
const OhipInputComponent = ({ theme, ...props }) => <OhipInput theme={theme} {...props} />;
const AlbertaInputComponent = ({ theme, ...props }) => <AlbertaInput theme={theme} {...props} />;
const NovaScotiaInputComponent = ({ theme, ...props }) => <NovaScotiaInput theme={theme} {...props} />;
const NoHealthCardSystemSelectedInputComponent = ({ theme, ...props }) => <NoHealthCardSystemSelectedInput theme={theme} {...props} disabled={true} />;

const VerifyAccountHealthCardInput = ({
    isVerificationAlreadySuccessful,
    renderAs,
    touched,
    errors,
    values,
    header,
    caption,
    healthCardPatternMatch,
    isWatchman,
}) => {
    const theme = useTheme();
    return (
        <>
            <Box mt={3} mb={1}>
                <Typography variant="body2">{header}</Typography>
            </Box>
            <Field
                name="healthCardNumber"
                as={renderAs}
                isinvalid={touched.healthCardNumber && (errors.healthCardNumber || !healthCardPatternMatch(values.healthCardSystem, values.healthCardNumber))}
                disabled={isVerificationAlreadySuccessful}
                isWatchman={isWatchman}
                theme={theme}
                filled={!!values.healthCardNumber}
            />
            <ErrorContainer
                shouldShow={touched.healthCardNumber && (errors.healthCardNumber || !healthCardPatternMatch(values.healthCardSystem, values.healthCardNumber))}
            >
                <ErrorTypography variant="caption" color="error">
                    {caption}
                </ErrorTypography>
            </ErrorContainer>
        </>
    );
};

const VerifyAccountDateInput = ({ isVerificationAlreadySuccessful, touched, errors, values, isWatchman }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(styleBreakMobile, { noSsr: true });
    return (
        <>
            <Box mt={3} mb={1}>
                <Typography variant="body2">Date of Birth</Typography>
            </Box>
            <FormDivSectionWrapper isMobile={isMobile}>
                <Box flexGrow={1}>
                    <MonthSelectorField
                        name="month"
                        disabled={isVerificationAlreadySuccessful}
                        filled={!!values.month}
                        isinvalid={touched.month && errors.month}
                    />
                </Box>
                <Box ml={1}>
                    <Field
                        name="date"
                        as={DateInput}
                        placeholder="DD"
                        isinvalid={touched.date && errors.date}
                        disabled={isVerificationAlreadySuccessful}
                        theme={theme}
                        isWatchman={isWatchman}
                        filled={!!values.date}
                    />
                </Box>
                <Box ml={1}>
                    <Field
                        name="year"
                        as={YearInput}
                        placeholder="YYYY"
                        isinvalid={touched.year && errors.year}
                        disabled={isVerificationAlreadySuccessful}
                        theme={theme}
                        isWatchman={isWatchman}
                        filled={!!values.year}
                    />
                </Box>
            </FormDivSectionWrapper>
            <ErrorContainer shouldShow={(touched.month && errors.month) || (touched.date && errors.date) || (touched.year && errors.year)}>
                <ErrorTypography variant="caption" color="error">
                    Please enter a valid date
                </ErrorTypography>
            </ErrorContainer>
        </>
    );
};

export const NedVerifyAccountLayout = ({ isVerificationAlreadySuccessful, values, touched, errors, healthCardPatternMatch }) => {
    return (
        <>
            <Box mt={3} mb={1}>
                <Typography variant="body2">Province</Typography>
            </Box>
            <HealthCardSystemSelectorField name="healthCardSystem" disabled={isVerificationAlreadySuccessful} />
            <VerifyAccountHealthCardInput
                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                renderAs={
                    values.healthCardSystem === HealthCardSystem.ALBERTA
                        ? AlbertaInputComponent
                        : values.healthCardSystem === HealthCardSystem.NOVA_SCOTIA
                        ? NovaScotiaInputComponent
                        : values.healthCardSystem === HealthCardSystem.ONTARIO
                        ? OhipInputComponent
                        : NoHealthCardSystemSelectedInputComponent
                }
                touched={touched}
                errors={errors}
                values={values}
                header={'Health Card Number'}
                caption={'Please enter a valid health card number'}
                healthCardPatternMatch={healthCardPatternMatch}
                isWatchman={false}
            />
            <VerifyAccountDateInput
                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                touched={touched}
                errors={errors}
                values={values}
                isWatchman={false}
            />
        </>
    );
};

export const WatchmanVerifyAccountLayout = ({ isVerificationAlreadySuccessful, values, touched, errors, healthCardPatternMatch }) => {
    return (
        <>
            <VerifyAccountDateInput
                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                touched={touched}
                errors={errors}
                values={values}
                isWatchman={true}
            />
            <VerifyAccountHealthCardInput
                isVerificationAlreadySuccessful={isVerificationAlreadySuccessful}
                renderAs={OhipInputComponent}
                touched={touched}
                errors={errors}
                values={values}
                header={'OHIP Number'}
                caption={'Please enter a valid OHIP number'}
                healthCardPatternMatch={healthCardPatternMatch}
                isWatchman={true}
            />
        </>
    );
};
